// Shared content for the SEO landing pages. FAQ text is the single source of
// truth for both the visible accordion and the FAQPage JSON-LD, so they always
// match exactly. Brand voice: calm, second person, no exclamation points, no
// dashes, no medical or treatment claims, no competitor names.

export const SEO_FAQS = {
  "/adhd-daily-planner": [
    {
      q: "Is this a paper planner or an app?",
      a: "You can use either. The free printable is paper. The full purchase includes the Notion Command Center system plus the Today web app, which you open in any browser at commandcenteradhd.com/today.",
    },
    {
      q: "Do I need a paid Notion account?",
      a: "No. The Notion Command Center works on the free Notion plan in any browser.",
    },
    {
      q: "What if I fall behind for a few days?",
      a: "That is expected. The planner is built to restart. You pick one thing today and carry forward only what still matters.",
    },
    {
      q: "Is this medical treatment?",
      a: "No. This is a planning system, not medical care or treatment. It helps you organize your day.",
    },
    {
      q: "How is this different from a plain to do list?",
      a: "A to do list treats every task as equal. This planner asks you to choose one priority first, then keeps the rest small and optional so your attention has somewhere to land.",
    },
  ],
  "/adhd-overwhelm": [
    {
      q: "Does Low Battery Mode delete my other tasks?",
      a: "No. Your time blocks and other quick wins are only hidden while you are in Low Battery Mode. Tap Return to full plan and everything is exactly where you left it. The Can wait note is a separate spot to park one thing for later.",
    },
    {
      q: "How long is the focus timer?",
      a: "You can choose five or ten minutes. The point is to start, so a short block is often better than a long one.",
    },
    {
      q: "Is this medical treatment?",
      a: "No. This is a planning system, not medical care. It gives your day a calmer shape when overwhelm hits.",
    },
    {
      q: "What if even one thing feels like too much?",
      a: "Then make the one thing smaller. Open the app for two minutes. Write a single sentence. The mode is designed to meet a low battery day where it is.",
    },
    {
      q: "Can I use this every day or only on bad days?",
      a: "Both. Many people check in each morning and only drop into Low Battery Mode when they need it.",
    },
  ],
  "/adhd-planner-no-subscription": [
    {
      q: "Is this really a one time payment?",
      a: "Yes. You pay $17 once. There is no subscription and no recurring charge.",
    },
    {
      q: "Are future updates extra?",
      a: "No. Future updates to the system are included in the one time price.",
    },
    {
      q: "Do I need to pay for Notion?",
      a: "No. The Notion Command Center works on the free Notion plan in any browser.",
    },
    {
      q: "What if it does not work for me?",
      a: "There is a 30 day money back guarantee, so you can try it with low risk.",
    },
    {
      q: "Is this medical treatment?",
      a: "No. It is a planning system, not medical care. It gives your day a calmer, simpler structure.",
    },
  ],
};

// Absolute URL of a real product screenshot (the Today web app).
export const PRODUCT_IMAGE = "https://commandcenteradhd.com/seo-today-daily.png";

// A complete Offer for Google merchant listings. Digital product delivered by
// email, so shipping is free and instant (0 day handling and transit) and the
// return policy is a 30 day free return by mail.
export const MERCHANT_OFFER = {
  "@type": "Offer",
  price: "17.00",
  priceCurrency: "USD",
  availability: "https://schema.org/InStock",
  url: "https://commandcenteradhd.com/get-started",
  itemCondition: "https://schema.org/NewCondition",
  hasMerchantReturnPolicy: {
    "@type": "MerchantReturnPolicy",
    applicableCountry: "US",
    returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
    merchantReturnDays: 30,
    returnFees: "https://schema.org/FreeReturn",
    refundType: "https://schema.org/FullRefund",
    merchantReturnLink: "https://commandcenteradhd.com/#faq",
  },
  shippingDetails: {
    "@type": "OfferShippingDetails",
    shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "USD" },
    shippingDestination: { "@type": "DefinedRegion", addressCountry: "US" },
    deliveryTime: {
      "@type": "ShippingDeliveryTime",
      handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
      transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 0, unitCode: "DAY" },
    },
  },
};

// Product JSON-LD used on the no subscription page.
export const PRODUCT_JSONLD = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "CommandCenterADHD",
  description:
    "An ADHD planning system with no subscription. It includes the Notion Command Center, which works on the free Notion plan, and the Today web app with a daily view, quick wins, honest time blocks, and Low Battery Mode.",
  image: PRODUCT_IMAGE,
  brand: { "@type": "Brand", name: "CommandCenterADHD" },
  offers: MERCHANT_OFFER,
};

export function faqPageJsonLd(path) {
  const faqs = SEO_FAQS[path];
  if (!faqs) return null;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
}
